/*
 * ET - PDP Product Details (sections/et-pdp-details.liquid)
 * <et-pdp-details-bundles>: the bundle cards are a static two-up grid from 750px and a Swiper slider below 750px
 * (280px slides from CSS, 16px gap, progress bar = Swiper Scrollbar on the shared .et-slider-progress__track, thumb sized
 * like the Figma, see fitScrollbar). Optional autoplay from the section's Slider settings (data-autoplay, data-autoplay-delay;
 * off by default, skipped for prefers-reduced-motion, paused while a bundle block is selected in the theme editor). Autoplay
 * pauses on mouse hover and stops for good once a visitor swipes, presses or tabs into the slider (WCAG 2.2.2).
 * Swiper is created/destroyed on the breakpoint change and destroyed on disconnect, so theme editor re-renders
 * (shopify:section:load / unload) never leave a duplicate instance. Blocks selected in the editor slide into view.
 *
 * Add to cart (bought together buttons and the bundle cards' cart buttons, both Impact product-form): with the theme cart
 * type "Drawer" Impact opens the ET cart drawer itself. With "Popover" it would open the theme's cart notification
 * instead, so for adds from this section the notification is blocked (Impact's blockCartDrawerOpening flag) and the
 * ET cart drawer (#cart-drawer) opens; its content still updates from the cart:change event that follows. Adds from a
 * bundle card's quick-buy drawer (products with several variants) are left to Impact, which shows its own added state.
 * The "Page" cart type redirects to /cart before any event, so nothing changes there.
 */
(function () {
  'use strict';

  if (window.etPdpDetailsLoaded) return;
  window.etPdpDetailsLoaded = true;

  const SECTION_SELECTOR = '.et-pdp-details';

  document.addEventListener(
    'variant:add',
    (event) => {
      const settings = window.themeVariables && window.themeVariables.settings;
      if (!settings || settings.cartType !== 'popover') return;
      if (!event.detail || event.detail.blockCartDrawerOpening) return;
      if (!(event.target instanceof Element) || !event.target.closest(SECTION_SELECTOR)) return;
      if (event.target.closest('quick-buy-drawer')) return;

      const cartDrawer = document.getElementById('cart-drawer');
      if (!cartDrawer || typeof cartDrawer.show !== 'function') return;

      event.detail.blockCartDrawerOpening = true;
      cartDrawer.show();
    },
    true
  );

  if (!window.customElements || window.customElements.get('et-pdp-details-bundles')) return;

  const SLIDER_QUERY = '(max-width: 749px)';
  const SLIDE_GAP = 16;
  const SLIDE_SELECTOR = '[data-et-pdp-details-slide]';

  /*
   * Progress thumb length = the slider's visible width as a share of every slide pitch (card + gap), in whole pixels.
   * Figma draws a 55px thumb in the 96px track for two 280px cards (96 x 342 / 592 = 55.46); Swiper's 'auto' size leaves
   * out the last gap (96 x 342 / 576 = 57px). Still proportional, so more or fewer bundles shorten or lengthen it.
   * Swiper's own scrollbar handlers run first on each event; this one then resizes the thumb and puts it back in place.
   */
  const fitScrollbar = (swiper) => {
    const scrollbar = swiper.scrollbar;
    if (!scrollbar || !scrollbar.el || !scrollbar.dragEl || typeof scrollbar.updateSize !== 'function') return;

    const trackSize = scrollbar.el.offsetWidth;
    const spacing = parseFloat(swiper.params.spaceBetween) || 0;
    const sizes = swiper.slidesSizesGrid || [];
    const total = sizes.reduce((sum, size) => sum + size + spacing, 0);
    if (!trackSize || !total || !swiper.size) return;

    const dragSize = Math.min(trackSize, Math.max(1, Math.round((trackSize * swiper.size) / total)));
    if (swiper.params.scrollbar.dragSize === dragSize) return;

    swiper.params.scrollbar.dragSize = dragSize;
    scrollbar.updateSize();
    scrollbar.setTranslate();
  };

  class EtPdpDetailsBundles extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.loading = false;
      this.pendingIndex = null;
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.stopAutoplay = this.stopAutoplay.bind(this);
    }

    connectedCallback() {
      this.addEventListener('focusin', this.stopAutoplay);
      this.addEventListener('pointerdown', this.stopAutoplay);
      this.media = window.matchMedia(SLIDER_QUERY);
      if (typeof this.media.addEventListener === 'function') {
        this.media.addEventListener('change', this.handleMediaChange);
      } else if (typeof this.media.addListener === 'function') {
        this.media.addListener(this.handleMediaChange);
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.handleMediaChange, { once: true });
      } else {
        this.handleMediaChange();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('focusin', this.stopAutoplay);
      this.removeEventListener('pointerdown', this.stopAutoplay);
      if (this.media) {
        if (typeof this.media.removeEventListener === 'function') {
          this.media.removeEventListener('change', this.handleMediaChange);
        } else if (typeof this.media.removeListener === 'function') {
          this.media.removeListener(this.handleMediaChange);
        }
      }
      document.removeEventListener('DOMContentLoaded', this.handleMediaChange);
      this.destroy();
    }

    handleMediaChange() {
      if (!this.isConnected) return;
      if (this.media && this.media.matches) this.init();
      else this.destroy();
    }

    init() {
      const swiperEl = this.querySelector('[data-et-pdp-details-swiper]');
      if (!swiperEl || this.swiper || this.loading || typeof window.etLoadSwiper !== 'function') return;
      if (!this.media || !this.media.matches) return;
      if (!swiperEl.querySelector(SLIDE_SELECTOR)) return;

      this.loading = true;
      window.etLoadSwiper().then((Swiper) => {
        this.loading = false;
        if (!Swiper || !this.isConnected || this.swiper || !this.media.matches) return;

        const track = swiperEl.querySelector('.swiper-wrapper');
        if (track) track.scrollLeft = 0;

        const progressEl = this.querySelector('[data-et-pdp-details-progress]');
        const options = {
          slidesPerView: 'auto',
          spaceBetween: SLIDE_GAP,
          speed: 400,
          watchOverflow: true,
          grabCursor: true,
          scrollbar: progressEl ? { el: progressEl, draggable: true } : false,
          a11y: { enabled: true },
          on: {
            init: fitScrollbar,
            update: fitScrollbar,
            resize: fitScrollbar,
            observerUpdate: fitScrollbar,
            lock: fitScrollbar,
            unlock: fitScrollbar
          }
        };

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (this.dataset.autoplay === 'true' && !reducedMotion) {
          options.autoplay = {
            delay: parseInt(this.dataset.autoplayDelay, 10) || 5000,
            disableOnInteraction: true,
            pauseOnMouseEnter: true
          };
        }

        this.swiper = new Swiper(swiperEl, options);

        if (this.pendingIndex !== null) {
          if (this.swiper.autoplay && this.swiper.autoplay.running) this.swiper.autoplay.stop();
          this.swiper.slideTo(this.pendingIndex, 0);
          this.pendingIndex = null;
        }
      });
    }

    destroy() {
      this.pendingIndex = null;
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;
      const progressEl = this.querySelector('[data-et-pdp-details-progress]');
      if (progressEl) progressEl.innerHTML = '';
    }

    selectSlide(slide) {
      const slides = Array.from(this.querySelectorAll(SLIDE_SELECTOR));
      const index = slides.indexOf(slide);
      if (index < 0) return;

      if (this.swiper) {
        if (this.swiper.autoplay && this.swiper.autoplay.running) this.swiper.autoplay.stop();
        this.swiper.slideTo(index, 0);
      } else if (this.media && this.media.matches) {
        this.pendingIndex = index;
      }
    }

    stopAutoplay() {
      if (this.swiper && this.swiper.autoplay && this.swiper.autoplay.running) this.swiper.autoplay.stop();
    }

    resumeAutoplay() {
      if (this.swiper && this.swiper.params.autoplay && this.swiper.params.autoplay.enabled && this.swiper.autoplay) {
        this.swiper.autoplay.start();
      }
    }
  }

  window.customElements.define('et-pdp-details-bundles', EtPdpDetailsBundles);

  const slidersIn = (scope) =>
    scope instanceof Element || scope === document ? Array.from(scope.querySelectorAll('et-pdp-details-bundles')) : [];

  document.addEventListener('shopify:section:load', (event) => slidersIn(event.target).forEach((el) => el.handleMediaChange()));
  document.addEventListener('shopify:section:unload', (event) => slidersIn(event.target).forEach((el) => el.destroy()));

  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target instanceof Element ? event.target.closest(SLIDE_SELECTOR) : null;
    const slider = slide && slide.closest('et-pdp-details-bundles');
    if (slider) slider.selectSlide(slide);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const slide = event.target instanceof Element ? event.target.closest(SLIDE_SELECTOR) : null;
    const slider = slide && slide.closest('et-pdp-details-bundles');
    if (slider) slider.resumeAutoplay();
  });
})();
